import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from 'lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        hajj: 'bg-hajj-primary hover:bg-hajj-secondary text-white rounded-md shadow-lg shadow-hajj-primary/20 hover:shadow-xl hover:shadow-hajj-primary/30 transition-all duration-200',
        'modal-primary':
          'border border-[#287c50] bg-[#287c50] text-white shadow-none hover:border-[#17613d] hover:bg-[#17613d] disabled:border-[#80aa91] disabled:bg-[#80aa91] disabled:text-white disabled:opacity-100',
        'modal-secondary': 'border border-[#d7dfda] bg-white text-[#33463b] shadow-none hover:bg-[#f4f7f5]',
        disabled: 'bg-gray-300 text-gray-600 cursor-not-allowed',
      },
      size: {
        default: 'h-9 px-2.5 py-2',
        sm: 'h-8 rounded-md px-2.5',
        lg: 'h-10 rounded-md px-2.5',
        icon: 'h-9 w-9',
        modal: 'min-h-9 rounded-[8px] px-[14px] py-[7px] text-sm leading-5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
});
Button.displayName = 'Button';

export { Button, buttonVariants };
